import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { useAccountCompanyId } from 'application/hooks';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { toast } from '@shared/ui';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import {
  AcceptanceAdvanceStatus,
  AcceptanceCancellationReason,
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  RequisiteRuPureType,
} from '@shared/api/graphql/generated';
import { useAcceptanceAdvancedContext } from '@entities/billing';

import { TEXTS } from './texts';

export const useAcceptanceCredsMismatchWatcher = (): void => {
  const { openModal } = useModalContext();

  const companyIdFromStore = useAccountCompanyId();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const companyId = companyIdFromStore || userData?.currentUserPureQuery?.mainCompany?.id || '';
  const currentUserId = userData?.currentUserPureQuery?.id;
  const currentRequisite = userData?.currentUserPureQuery?.mainCompany?.requisite as RequisiteRuPureType;

  const { acceptanceAdvanced, markAcceptanceErrorAsViewed } = useAcceptanceAdvancedContext(companyId);

  const status = acceptanceAdvanced?.status || null;
  const cancellationReason = acceptanceAdvanced?.cancellationReason;
  const acceptanceUserId = acceptanceAdvanced?.userId;
  const errorViewedAt = acceptanceAdvanced?.errorViewedAt;
  const payerInn = acceptanceAdvanced?.payerInn ?? '';
  const payerName = acceptanceAdvanced?.payerName ?? '';

  useEffect(() => {
    if (status !== AcceptanceAdvanceStatus.Canceled) {
      return;
    }

    if (cancellationReason !== AcceptanceCancellationReason.CredentialsMismatch) {
      return;
    }

    const acceptanceId = acceptanceAdvanced?.id ?? '';
    const shouldShow = acceptanceUserId === currentUserId && !errorViewedAt;

    if (!shouldShow) {
      return;
    }

    openModal(
      ModalTypes.ACCEPTANCE_CREDS_MISMATCH_ERROR,
      {
        payerInn,
        payerName,
        companyInn: currentRequisite?.inn,
        companyName: currentRequisite?.organizationFullname,
      },
      async () => {
        await markAcceptanceErrorAsViewed(companyId, acceptanceId);

        toast.info(TEXTS.SBER_BUSINESS_ONLINE_CREDS_MISMATCH_CANCELLED, { duration: 10000 });
      }
    );
  }, [status]);
};
